/* eslint-disable i18next/no-literal-string */
// @noflow
import { useReactiveVar } from '@apollo/client'
import React, { useCallback, useState } from 'react'

import { countryCodeToLocaleCurrency } from '@/utils/countryCodeHelper'
import { formatCurrencyWithDecimal } from '@/utils/currency'

import Link from '@/components/elements/atoms/Link/Link'
import Text from '@/components/elements/atoms/Text/Text'
import { checkoutPageState } from '@/components/pages/CheckoutPage/CheckoutPage'

import STYLES from './BoxBreakdownSummary.module.sass'

import { checkoutPricingState } from '../../../../hooks/useCheckoutPricing'
import OrderSummaryRowItem from '../OrderSummaryRowItem/OrderSummaryRowItem'
import SelectedRecipesModal from '../SelectedRecipesModal/SelectedRecipesModal'

type Props = {
  namespace: string
  shouldDisplayPrice: boolean
}

const BoxBreakdownSummary = ({
  namespace,
  shouldDisplayPrice
}: Props): JSX.Element => {
  const copyContext = 'order_summary'
  const [openModal, setOpenModal] = useState(false)
  const { plan, user } = useReactiveVar(checkoutPageState)

  const { firstOrderPricing, standardOrderPricing, productVariants } =
    useReactiveVar(checkoutPricingState)

  const { shippingCountryCode, preferredLanguage } = user

  const { locale, currency } = countryCodeToLocaleCurrency(
    shippingCountryCode,
    preferredLanguage
  )

  const { trialLengthInDays, pouchesPerDay, durationInDays } = plan
  const { funnelGiftCampaign } = user

  const {
    netTotalPrice,
    netCoreFoodPrice,
    totalSavedAmount,
    netDeliverySurchargePrice,
    standardOrderCoreFoodGrossPrice
  } = firstOrderPricing

  const { netDeliverySurchargePrice: standardNetDeliverySurchargePrice } =
    standardOrderPricing

  const hasFreeDelivery = netDeliverySurchargePrice === 0

  const totalPouches = trialLengthInDays * pouchesPerDay

  const savingsApplied = totalSavedAmount > 0

  const price = shouldDisplayPrice ? netTotalPrice : 0

  const handleViewPouches = useCallback(() => {
    setOpenModal(true)
  }, [])

  return (
    <div className={STYLES.container}>
      <Text
        namespace={namespace}
        text={`${copyContext}.box_breakdown_summary.title`}
        variant="textRegular20"
        margin={false}
        align="left"
        bold
        element="h2"
      />
      <OrderSummaryRowItem
        label={{
          namespace,
          text: `${copyContext}.summary.total_pouches`,
          variables: {
            totalPouches
          }
        }}
        price={
          savingsApplied ? standardOrderCoreFoodGrossPrice : netCoreFoodPrice
        }
        discountedPrice={savingsApplied ? netCoreFoodPrice : null}
        locale={locale}
        currency={currency}
        variant="v2"
      />
      <div className={STYLES.viewPouches}>
        <Link
          text={`${copyContext}.box_breakdown_summary.view_pouches`}
          onClick={handleViewPouches}
          namespace={namespace}
          suffix={null}
          shouldScale={false}
          identifier={`box_breakdown_summary.view_your_pouches`}
          variant="leftFlush"
        />
      </div>

      {productVariants?.map((product) => (
        <OrderSummaryRowItem
          key={product.productVariant.productCollection.name}
          label={{
            text: product.productVariant.productCollection.name,
            translate: false
          }}
          price={product.productVariant.recurringDeliveryType?.netPrice || 0}
          discountedPrice={product.discountedPrice || undefined}
          locale={locale}
          currency={currency}
          variant="v2"
        />
      ))}
      {funnelGiftCampaign && funnelGiftCampaign.productVariant && (
        <OrderSummaryRowItem
          key={funnelGiftCampaign.productVariant.productCollection.name}
          label={{
            text: funnelGiftCampaign.productVariant.productCollection.name,
            translate: false
          }}
          price={
            funnelGiftCampaign.productVariant.recurringDeliveryType?.netPrice ||
            0
          }
          discountedPrice={0}
          locale={locale}
          currency={currency}
          variant="v2"
          isFree
        />
      )}
      <OrderSummaryRowItem
        label={{
          namespace,
          text: `${copyContext}.summary.delivery`
        }}
        price={standardNetDeliverySurchargePrice}
        discountedPrice={netDeliverySurchargePrice}
        locale={locale}
        currency={currency}
        isFree={hasFreeDelivery}
        variant="v2"
      />
      <div className={STYLES.dividerLight} />
      <div className={`${STYLES.footerSection} ${STYLES.boxTotal}`}>
        <div>
          <Text
            text={`${copyContext}.box_breakdown_summary.starter_box_total`}
            namespace={namespace}
            variant="textRegular18"
            margin={false}
            align="left"
            bold
          />
          <Text
            text={`${copyContext}.box_breakdown_summary.you_save`}
            variables={{
              totalDiscountedAmount: formatCurrencyWithDecimal(
                totalSavedAmount,
                {
                  locale,
                  currency
                }
              )
            }}
            namespace={namespace}
            margin={false}
            align="left"
            colour="supportGreen400"
          />
        </div>
        <Text
          text={formatCurrencyWithDecimal(price, {
            locale,
            currency
          })}
          translate={false}
          variant="textRegular18"
          margin={false}
          bold
        />
      </div>
      <div className={STYLES.divider} />
      <div className={STYLES.footerSection}>
        <Text
          text={`${copyContext}.box_breakdown_summary.ongoing_boxes`}
          namespace={namespace}
          variant="textRegular16"
          margin={false}
        />
        <Text
          text={`${copyContext}.box_breakdown_summary.per_day`}
          namespace={namespace}
          variables={{
            pricePerDay: formatCurrencyWithDecimal(
              standardOrderPricing.netTotalPricePerDay,
              {
                locale,
                currency
              }
            )
          }}
          variant="textRegular16"
          margin={false}
          bold
        />
      </div>
      <div className={STYLES.footerSection}>
        <Text
          text={`${copyContext}.recipes_modal.footer_note`}
          namespace={namespace}
          margin={false}
          align="left"
          variant="textRegular14"
          colour="brandBlue400"
          variables={{
            cadenceInDays: durationInDays
          }}
        />
        {shippingCountryCode === 'BE' && (
          <Text
            text={`${copyContext}.box_breakdown_summary.total`}
            namespace={namespace}
            variables={{
              totalPrice: formatCurrencyWithDecimal(
                standardOrderPricing.netTotalPrice,
                {
                  locale,
                  currency
                }
              )
            }}
            margin={false}
            variant="textRegular14"
          />
        )}
      </div>
      <SelectedRecipesModal
        namespace={namespace}
        isOpen={openModal}
        toggle={setOpenModal}
      />
    </div>
  )
}

export default BoxBreakdownSummary
